import { useMemo } from "react";
import { useIntl } from "react-intl";
import { useWatch } from "react-hook-form";
import { useColumnOrder, useTable } from "react-table";

import { useGetPackingListConfig } from "api/queries/location/useGetPackingListConfig";
import Card from "components/v2/Card/Card";
import testIds from "constants/testIds";
import DataTable from "components/v2/Table/DataTable";
import type { PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import { buildHandlingUnitHashMap } from "@RequestFormV2/sections/PackingList/utils/buildHandlingUnitHashMap";
import { getDangerousGoodsOptionsV2 } from "@RequestFormV2/sections/PackingList/utils/dangerousGoodsOptions";
import { getTemperatureOptionsV2 } from "@RequestFormV2/sections/PackingList/utils/temperatureOptions";
import {
  buildReviewPackingListRows,
  hasReviewPackingListRows,
} from "@RequestFormV2/components/ReviewPage/sections/ReviewPackingList/buildReviewPackingListRows";
import { useReviewPackingListColumns } from "@RequestFormV2/components/ReviewPage/sections/ReviewPackingList/reviewPackingListColumns";
import { useReviewStopDisplay } from "@RequestFormV2/components/ReviewPage/sections/ReviewPackingList/useReviewStopDisplay";
import ReviewPackingListSummary from "@RequestFormV2/components/ReviewPage/sections/ReviewPackingList/ReviewPackingListSummary";
import {
  getPackingListWorkflowColumnFlags,
  isPackingListWorkflowConfigReady,
} from "@RequestFormV2/sections/PackingList/utils/packingListWorkflowFlags";

export default function ReviewPackingList() {
  const intl = useIntl();
  const packingList = (useWatch({ name: "packing_list" }) as PackingListRow[] | undefined) ?? [];
  const location = useWatch({ name: "organizational.location" });

  const { data: packingListConfig, isLoading: isLoadingPackingListConfig } = useGetPackingListConfig(location ?? "", {
    enabled: !!location,
  });

  const isWorkflowConfigReady = isPackingListWorkflowConfigReady(location, isLoadingPackingListConfig);
  const workflowColumnFlags = useMemo(
    () => getPackingListWorkflowColumnFlags(packingListConfig),
    [packingListConfig],
  );

  const handlingUnitHashMap = useMemo(
    () => buildHandlingUnitHashMap(packingListConfig?.handling_unit_options),
    [packingListConfig?.handling_unit_options],
  );

  const containerTypeOptions = useMemo(() => {
    const containerEntry = packingListConfig?.handling_unit_options?.find((item) => "container" in item);
    const container = containerEntry?.container as { handling_unit_options?: { key: string; value: string }[] } | undefined;
    return (
      container?.handling_unit_options?.map((option) => ({
        label: option.value,
        value: option.key,
      })) ?? []
    );
  }, [packingListConfig?.handling_unit_options]);

  const labelContext = useMemo(
    () => ({
      intl,
      handlingUnitHashMap,
      containerTypeOptions,
      dangerousGoodsOptions: getDangerousGoodsOptionsV2(intl),
      temperatureOptions: getTemperatureOptionsV2(intl),
      hazardousEnabled: isWorkflowConfigReady && workflowColumnFlags.hazardousEnabled,
      temperatureEnabled: isWorkflowConfigReady && workflowColumnFlags.temperatureEnabled,
      dangerousEnabled: isWorkflowConfigReady && workflowColumnFlags.dangerousEnabled,
      cratingEnabled: isWorkflowConfigReady && workflowColumnFlags.cratingEnabled,
    }),
    [intl, handlingUnitHashMap, containerTypeOptions, isWorkflowConfigReady, workflowColumnFlags],
  );

  const { isMultiStop, stopDisplayByKey } = useReviewStopDisplay();

  const tableData = useMemo(() => buildReviewPackingListRows(packingList), [packingList]);
  const hasRows = hasReviewPackingListRows(packingList);
  const columns = useReviewPackingListColumns(intl, labelContext, isMultiStop, stopDisplayByKey);
  const tableInstance = useTable({ columns, data: tableData }, useColumnOrder);

  return (
    <Card borderRadius="xl" shadow="xs" data-testid={testIds.REQUEST_FORM_REVIEW_PACKING_LIST}>
      <Card.Header
        title={intl.formatMessage({ id: "request-form.packingList", defaultMessage: "Packing List" })}
        showBorder
      />
      <Card.Body>
        <div className="rfv2-review__section-content rfv2-review-packing-list">
          {!hasRows ? (
            <span className="rfv2-review__empty">
              {intl.formatMessage({
                id: "request-form.review.packingList.empty",
                defaultMessage: "No packing list items added",
              })}
            </span>
          ) : (
            <>
              <ReviewPackingListSummary packingList={packingList} />
              <DataTable tableInstance={tableInstance} tableLayout="fixed" />
            </>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
